import cn from 'classnames';
import { format, intlFormat } from 'date-fns';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { satisfies } from 'semver';
import { ZodError } from 'zod';
import { TimeoutError, trackError, type ErrorDetails } from '#api/errors.ts';
import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { sendCommand } from '#api/hosts.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Code } from '#components/atoms/code/Code.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { generateFilename } from '#util/generateFilename.ts';
import { formatRFC3339 } from '#util/time.ts';
import { calculateStreamState } from '../calculateStreamState.ts';
import { DEBUG_MIN_VERSION } from '../constants.ts';
import type { Log, LogStatus } from '../worker/log-worker-types.ts';
import { DebugButtons } from './buttons/DebugButtons.tsx';
import { CreateTunnelDialog } from './command-dialogs/CreateTunnelDialog.tsx';
import { PrintCertDialog } from './command-dialogs/PrintCertDialog.tsx';
import { PrintTunnelDialog } from './command-dialogs/PrintTunnelDialog.tsx';
import { QueryLighthouseDialog } from './command-dialogs/QueryLighthouseDialog.tsx';
import { RestartDialog } from './command-dialogs/RestartDialog.tsx';
import type { CommandModal } from './DebugCommandMenu.tsx';
import { CommandError, useSuspenseDNClientCommandsQuery } from './DebugConsole.queries.ts';
import { DebugEmpty } from './empty/DebugEmpty.tsx';
import { sortLog } from './logstream/DebugLine.tsx';
import { LogStream } from './logstream/LogStream.tsx';
import { DebugStatus } from './status/DebugStatus.tsx';
import { DebugBlocked, DebugMobile, DebugTooOld, DebugUnenrolled } from './unsupported/index.ts';
import styles from './DebugConsole.module.css';

/**
 * Ideally this would be thrown by {@link useLogWorker}, but that requires moving it into
 * {@link DebugConsoleInner}, which would make DebugConsole harder to mock/test.
 */
class UnsupportedBrowserError extends Error {
  feature: string;

  constructor(feature: string) {
    super();
    this.feature = feature;
    this.name = 'UnsupportedBrowserError';
    this.message = `Browser does not support ${feature}`;
  }
}

type Props = {
  hostId: string;
  logs: Array<Log | string>;
  errors: ErrorDetails[];
  className?: string;
  onStartStream: () => void;
  status?: LogStatus;
  sessionStart: Date | null;
  sessionEnd: Date | null;
};

export function DebugConsole(props: Props) {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        if (error instanceof TimeoutError) {
          return (
            <div
              className={cn(props.className, styles.DebugConsole, styles.DebugConsole___empty)}
              data-user-color-scheme="dark"
            >
              <div className="center flex-col gap-8 height-100">
                <p className="h5 mb-8">Unable to connect</p>
                <p className="p-md mb-16">Please be sure dnclient is running on the host</p>

                <LinkButton variant="secondary" to="." reloadDocument={true}>
                  Retry
                </LinkButton>
              </div>
            </div>
          );
        }

        if (error instanceof ZodError) {
          return (
            <div
              className={cn(props.className, styles.DebugConsole, styles.DebugConsole___empty)}
              data-user-color-scheme="dark"
            >
              <div className="center flex-col gap-8 height-100">
                <p className="h5 mb-16">This dnclient doesnʼt seem to support debugging. Try again?</p>

                <LinkButton variant="secondary" to="." reloadDocument={true}>
                  Retry
                </LinkButton>
              </div>
            </div>
          );
        }

        if (error instanceof CommandError) {
          return (
            <div
              className={cn(props.className, styles.DebugConsole, styles.DebugConsole___empty)}
              data-user-color-scheme="dark"
            >
              <div className="center flex-col gap-8 height-100">
                <p className="h5 mb-16">dnclient returned an error. Try again?</p>
                <Code className="mb-16">Error message: {error.message}</Code>

                <LinkButton variant="secondary" to="." reloadDocument={true}>
                  Retry
                </LinkButton>
              </div>
            </div>
          );
        }

        if (error instanceof UnsupportedBrowserError) {
          return (
            <div
              className={cn(props.className, styles.DebugConsole, styles.DebugConsole___empty)}
              data-user-color-scheme="dark"
            >
              <div className="center flex-col gap-8 height-100">
                <p className="h5 mb-16">Unsupported browser</p>
                <p className="p-md">Debugging requires a browser with support for {error.feature}</p>
              </div>
            </div>
          );
        }

        // This is an unexpected error, track it.
        trackError(error);
        return (
          <div
            className={cn(props.className, styles.DebugConsole, styles.DebugConsole___empty)}
            data-user-color-scheme="dark"
          >
            <div className="center flex-col gap-8 height-100">
              <p className="h5 mb-16">Unknown error encountered. Try again?</p>
              <Code className="mb-16">Error message: {error.message}</Code>
              <LinkButton variant="secondary" to="." reloadDocument={true}>
                Retry
              </LinkButton>
            </div>
          </div>
        );
      }}
    >
      <Suspense
        fallback={
          <div className={cn(styles.DebugConsole, styles.DebugConsole___empty)} data-user-color-scheme="dark">
            <LoadingSpinner />
          </div>
        }
      >
        <DebugConsoleInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function stringifyLog(log: Log | string): string {
  return `${Object.entries(log)
    .toSorted(sortLog)
    .map(([key, val]) => `${key}=${typeof val === 'string' ? val : JSON.stringify(val, null, 2)}`)
    .join(' ')}\n`;
}

/** We wrap the {@link DebugConsoleInner} to add a suspense boundary */
function DebugConsoleInner({
  hostId,
  status,
  onStartStream,
  sessionStart,
  sessionEnd,
  className,
  logs,
  errors,
}: Props) {
  const [debugElement, setDebugElement] = useState<HTMLDivElement | null>(null);

  const {
    data: { data: host },
  } = useSuspenseHostQuery(hostId);
  const {
    data: { commands },
  } = useSuspenseDNClientCommandsQuery(hostId, host.metadata.version, host.metadata.platform);
  const [commandModal, setCommandModal] = useState<CommandModal | null>(null);
  const [commandResults, setCommandResults] = useState<Array<[number, Log]>>([]);
  const [streamStateMessages, setStreamStateMessages] = useState<Array<[number, string]>>([]);
  const streamState = calculateStreamState({ sessionStart, sessionEnd, errors });
  // Need to keep a cached state so that we only add messages when the state changes
  const cachedStreamState = useRef<(typeof streamState)['state']>(streamState.state);

  // This is used by useLogWorker, and we need to show an error screen if it's not supported
  if (typeof globalThis.SharedWorker === 'undefined') {
    throw new UnsupportedBrowserError('SharedWorker');
  }

  const isMobile = host.metadata.platform === 'mobile';
  const isEmpty =
    status === 'error' ?
      logs.length === 0
    : logs.length === 0 && commandResults.length === 0 && streamStateMessages.length === 0;
  const isStreaming = status === 'fetching';
  const isTooOld = Boolean(host.metadata.version && satisfies(host.metadata.version, `< ${DEBUG_MIN_VERSION}`));
  const isUnenrolled = host.metadata.lastSeenAt === null;
  const isBlocked = host.isBlocked;
  const isUnsupported = isMobile || isTooOld || isUnenrolled || isBlocked || commands.length === 0;

  // This creates an anchor tag with a blob url of the log contents, then clicks it and cleans up.
  // We do it this way so we can avoid updating the download blob each time a new log line is added.
  function handleDownload() {
    const stringifiedLogs: string[] = [];

    // Add header details
    stringifiedLogs.push(`orgID: ${host.organizationID}\n`);
    stringifiedLogs.push(`hostID: ${host.id}\n`);
    stringifiedLogs.push(`dnclient: ${host.metadata.version}\n`);
    stringifiedLogs.push(`start: ${sessionStart?.toISOString() ?? 'N/A'}\n`);
    stringifiedLogs.push(`end: ${sessionEnd?.toISOString() ?? 'N/A'}\n`);
    stringifiedLogs.push(`errors: ${JSON.stringify(errors)}\n`);
    stringifiedLogs.push('\n');

    // Join together logs and command results (similar to LogStream logic)
    const allMessages = [
      ...commandResults.filter(([i]) => i === 0).map(([, r]) => stringifyLog(r)),
      ...logs.map(
        (l, index) =>
          `${stringifyLog(l)}${commandResults
            .filter(([i]) => i === index + 1)
            .map(([, r]) => stringifyLog(r))
            .join('')}`
      ),
    ];

    // Add logs, command results, stream state messages
    stringifiedLogs.push(...allMessages);

    const fileBlob = new Blob(stringifiedLogs, {
      type: 'text/plain',
    });
    const blobURL = URL.createObjectURL(fileBlob);
    const saver = document.createElement('a');
    saver.href = blobURL;
    saver.download = generateFilename(['dnlog', formatRFC3339(Date.now()), hostId], 'txt');
    document.body.appendChild(saver);
    saver.dispatchEvent(new MouseEvent('click'));
    document.body.removeChild(saver);
    URL.revokeObjectURL(blobURL);
  }

  function appendCommandResult(result: Record<string, unknown>) {
    setCommandResults((prev) => {
      return prev.concat([[logs.length, result]]);
    });
  }

  function closeModal() {
    setCommandModal(null);
  }

  async function onDebugStack() {
    try {
      const result = await sendCommand(hostId, 'DebugStack');
      appendCommandResult(result);
    } catch (error) {
      appendCommandResult({ error });
    }
  }

  const appendStreamStateMessage = useCallback(
    (messageType: 'start' | 'stop' | 'disconnect', at: Date, logsCount: number) => {
      const datetime = `${intlFormat(at, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })} ${format(at, 'h:mm aa')}`;

      switch (messageType) {
        case 'start':
          setStreamStateMessages((prev) =>
            prev.concat([
              [logsCount, `Started at ${datetime}`],
              [logsCount, ''],
            ])
          );
          break;
        case 'stop':
          setStreamStateMessages((prev) =>
            prev.concat([
              [logsCount, ''],
              [logsCount, `Ended at ${datetime}`],
            ])
          );
          break;
        case 'disconnect':
          setStreamStateMessages((prev) =>
            prev.concat([
              [logsCount, ''],
              [logsCount, `Lost connection at ${datetime}`],
            ])
          );
          break;
        default:
          break;
      }
    },
    []
  );
  useEffect(() => {
    if (streamState.state === cachedStreamState.current) return;

    // Add a status message
    switch (streamState.state) {
      case 'started':
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStreamStateMessages([]);
        setCommandResults([]);
        appendStreamStateMessage('start', streamState.at, logs.length);
        cachedStreamState.current = streamState.state;
        break;
      case 'ended':
        // When returning to a previous page with data, the status updates before the logs,
        // so we don't want to put the "ended" message up at the top
        if (cachedStreamState.current === 'init' && logs.length === 0) break;

        appendStreamStateMessage('stop', streamState.at, logs.length);
        cachedStreamState.current = streamState.state;
        break;
      case 'disconnected':
        appendStreamStateMessage('disconnect', streamState.at, logs.length);
        cachedStreamState.current = streamState.state;
        break;
      case 'init':
      default:
        // no-op
        break;
    }
  }, [logs.length, streamState, appendStreamStateMessage]);

  return (
    <div
      className={cn(className, styles.DebugConsole, { [styles.DebugConsole___empty]: isUnsupported })}
      ref={setDebugElement}
      data-user-color-scheme="dark"
    >
      {isMobile ?
        <DebugMobile />
      : isTooOld ?
        <DebugTooOld />
      : isUnenrolled ?
        <DebugUnenrolled />
      : isBlocked ?
        <DebugBlocked />
      : <>
          {/** Command Dialogs */}
          {commandModal === 'CreateTunnel' ?
            <CreateTunnelDialog
              debugElement={debugElement}
              onSuccess={(result) => {
                appendCommandResult(result);
                closeModal();
              }}
              onClose={closeModal}
              host={host}
            />
          : commandModal === 'PrintTunnel' ?
            <PrintTunnelDialog
              debugElement={debugElement}
              onSuccess={(result) => {
                appendCommandResult(result);
                closeModal();
              }}
              onClose={closeModal}
              host={host}
            />
          : commandModal === 'PrintCert' ?
            <PrintCertDialog
              debugElement={debugElement}
              onSuccess={(result) => {
                appendCommandResult(result);
                closeModal();
              }}
              onClose={closeModal}
              host={host}
            />
          : commandModal === 'QueryLighthouse' ?
            <QueryLighthouseDialog
              debugElement={debugElement}
              onSuccess={(result) => {
                appendCommandResult(result);
                closeModal();
              }}
              onClose={closeModal}
              host={host}
            />
          : commandModal === 'Restart' ?
            <RestartDialog
              debugElement={debugElement}
              onSuccess={(result) => {
                appendCommandResult(result);
                closeModal();
              }}
              onClose={closeModal}
              host={host}
            />
          : null}
          <div className={styles.DebugConsole_header}>
            {isEmpty && !isStreaming ?
              <div />
            : <>
                <DebugStatus
                  onRestart={onStartStream}
                  sessionStart={sessionStart}
                  sessionEnd={sessionEnd}
                  errors={errors}
                />
                <DebugButtons
                  supportedCommands={commands}
                  onDownload={logs.length || commandResults.length ? handleDownload : undefined}
                  setCommandModal={setCommandModal}
                  onStartStream={isStreaming ? undefined : onStartStream}
                  onDebugStack={onDebugStack}
                />
              </>
            }
          </div>
          <div className={styles.DebugConsole_logStream}>
            {isEmpty ?
              // Empty state
              <DebugEmpty
                status={status}
                errors={errors}
                supportedCommands={commands}
                onStartStream={onStartStream}
                onDebugStack={onDebugStack}
                setCommandModal={setCommandModal}
              />
            : <LogStream commandResults={commandResults} streamStateMessages={streamStateMessages} logs={logs} />}
          </div>
        </>
      }
    </div>
  );
}
